import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, QrCode, Copy, Check, Download, Link2, Phone,
  ExternalLink, Mail, Share2, Plus, Trash2, Edit2, X
} from "lucide-react";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

const ICON_MAP = {
  qr: QrCode,
  phone: Phone,
  link: Link2,
  external: ExternalLink,
  mail: Mail,
  share: Share2,
};

const COLOR_MAP = {
  blue: "bg-blue-100 text-blue-600",
  amber: "bg-amber-100 text-amber-600",
  green: "bg-green-100 text-green-600",
  purple: "bg-purple-100 text-purple-600",
  red: "bg-red-100 text-red-600",
  teal: "bg-teal-100 text-teal-600",
  rose: "bg-rose-100 text-rose-600",
  cyan: "bg-cyan-100 text-cyan-600",
};

const EMPTY_FORM = { name: "", description: "", utm_source: "", show_qr: true, icon: "qr", color: "blue" };

const getSessionToken = () => localStorage.getItem("session_token");

export default function CampaignLinks() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(null);

  // Add/Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const init = async () => {
      const token = getSessionToken();
      if (!token) {
        navigate("/login");
        return;
      }
      try {
        const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setUser(me.data);
        await loadCampaigns();
      } catch (_e) {
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const loadCampaigns = async () => {
    const token = getSessionToken();
    try {
      const { data } = await axios.get(`${API}/campaign-links`, { headers: { Authorization: `Bearer ${token}` } });
      setCampaigns(data || []);
    } catch (_e) {
      toast.error("Failed to load campaigns");
    }
  };

  const getTrackableLink = (utm) => `${FRONTEND_URL}/quote?utm_source=${utm}`;

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(id);
      toast.success("Link copied!");
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (_e) {
      toast.error("Copy failed");
    }
  };

  const downloadQRCode = async (utm) => {
    const link = getTrackableLink(utm);
    const qrUrl = `${API}/qr?data=${encodeURIComponent(link)}&size=800&download=true&filename=screenzz-qr-${utm}`;
    try {
      const response = await fetch(qrUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `screenzz-qr-${utm}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("QR code downloaded!");
    } catch (_e) {
      toast.error("Failed to download QR code");
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      description: c.description || "",
      utm_source: c.utm_source || "",
      show_qr: c.show_qr !== false,
      icon: c.icon || "qr",
      color: c.color || "blue",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.utm_source.trim()) {
      toast.error("Name and UTM source are required");
      return;
    }
    setSaving(true);
    const token = getSessionToken();
    try {
      if (editing) {
        await axios.put(`${API}/campaign-links/${editing.campaign_id}`, form,
          { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Campaign updated");
      } else {
        await axios.post(`${API}/campaign-links`, form,
          { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Campaign added");
      }
      setDialogOpen(false);
      await loadCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const token = getSessionToken();
    try {
      await axios.delete(`${API}/campaign-links/${deleteTarget.campaign_id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Campaign deleted");
      setDeleteTarget(null);
      await loadCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  const isAdmin = user.role === "Admin";

  return (
    <div className="min-h-screen bg-white">
      <Header user={user} />

      <div className="max-w-lg mx-auto pb-24 p-4">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-inter">Back to Settings</span>
        </button>

        <div className="flex items-start justify-between mb-6 gap-3">
          <div>
            <h1 className="font-barlow text-3xl font-bold text-zinc-950 uppercase mb-2">Campaign Links</h1>
            <p className="font-inter text-zinc-600">Trackable links for marketing campaigns</p>
          </div>
          {isAdmin && (
            <button
              onClick={openCreate}
              data-testid="new-campaign-button"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white text-sm font-semibold whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              New
            </button>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>How it works:</strong> Each link tracks where your leads come from.
            When someone submits an estimate through these links, you&apos;ll see the source in your Lead Analytics.
          </p>
        </div>

        <div className="space-y-4">
          {campaigns.map((c) => {
            const Icon = ICON_MAP[c.icon] || QrCode;
            const colorClass = COLOR_MAP[c.color] || COLOR_MAP.blue;
            const link = getTrackableLink(c.utm_source);
            return (
              <div
                key={c.campaign_id}
                className="bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5"
                data-testid={`campaign-${c.utm_source}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`h-12 w-12 rounded-lg ${colorClass} flex items-center justify-center shrink-0`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-inter text-lg font-bold text-zinc-900">{c.name}</h3>
                    {c.description && <p className="text-sm text-zinc-600">{c.description}</p>}
                  </div>
                  {isAdmin && (
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => openEdit(c)}
                        data-testid={`edit-campaign-${c.utm_source}`}
                        className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(c)}
                        data-testid={`delete-campaign-${c.utm_source}`}
                        className="p-1.5 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="bg-zinc-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-zinc-500 mb-1">Trackable Link:</p>
                  <p className="font-mono text-sm text-zinc-800 break-all">{link}</p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => copyToClipboard(link, c.campaign_id)}
                    className="flex-1 flex items-center justify-center gap-2 h-10 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-medium text-sm"
                    data-testid={`copy-${c.utm_source}`}
                  >
                    {copiedLink === c.campaign_id ? (
                      <><Check className="w-4 h-4 text-green-600" /><span className="text-green-600">Copied!</span></>
                    ) : (
                      <><Copy className="w-4 h-4" /><span>Copy Link</span></>
                    )}
                  </button>
                  {c.show_qr && (
                    <button
                      onClick={() => downloadQRCode(c.utm_source)}
                      className="flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-medium text-sm"
                      data-testid={`download-qr-${c.utm_source}`}
                    >
                      <Download className="w-4 h-4" />
                      <span>QR Code</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {campaigns.length === 0 && (
            <div className="text-center py-12 bg-zinc-50 rounded-xl border-2 border-dashed border-zinc-200">
              <QrCode className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">No campaigns yet.</p>
              {isAdmin && <p className="text-zinc-400 text-xs mt-1">Tap &quot;New&quot; to create your first trackable link.</p>}
            </div>
          )}
        </div>

        <div className="mt-6 bg-zinc-50 rounded-lg p-4">
          <h4 className="font-inter font-semibold text-zinc-900 mb-2">Pro Tip</h4>
          <p className="text-sm text-zinc-600">
            For business cards, you can use a URL shortener (like bit.ly) to create a shorter,
            more memorable link. The tracking will still work!
          </p>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="campaign-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl uppercase">
              {editing ? "Edit Campaign" : "New Campaign"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {editing ? "Edit campaign details" : "Create a new trackable campaign link"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-semibold text-zinc-700 mb-1 block">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Facebook Ad — Fall 2026"
                data-testid="campaign-name-input"
                className="w-full h-11 px-3 rounded-lg border-2 border-zinc-200 focus:border-[#207024] focus:outline-none"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-zinc-700 mb-1 block">Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Where this link goes"
                data-testid="campaign-description-input"
                className="w-full h-11 px-3 rounded-lg border-2 border-zinc-200 focus:border-[#207024] focus:outline-none"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-zinc-700 mb-1 block">
                UTM Source *
                <span className="text-xs text-zinc-400 font-normal ml-1">(letters, numbers, dash, underscore)</span>
              </label>
              <input
                type="text"
                value={form.utm_source}
                onChange={(e) => setForm({ ...form, utm_source: e.target.value.toLowerCase().replace(/[^a-z0-9_\-]+/g, "_") })}
                placeholder="fb_fall_2026"
                data-testid="campaign-utm-input"
                className="w-full h-11 px-3 rounded-lg border-2 border-zinc-200 focus:border-[#207024] focus:outline-none font-mono text-sm"
              />
              {form.utm_source && (
                <p className="text-xs text-zinc-500 mt-1 break-all">
                  Preview: <span className="font-mono">{getTrackableLink(form.utm_source)}</span>
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-zinc-700 mb-1 block">Icon</label>
                <select
                  value={form.icon}
                  onChange={(e) => setForm({ ...form, icon: e.target.value })}
                  data-testid="campaign-icon-select"
                  className="w-full h-11 px-3 rounded-lg border-2 border-zinc-200 bg-white"
                >
                  <option value="qr">QR Code</option>
                  <option value="phone">Phone</option>
                  <option value="link">Link</option>
                  <option value="external">External</option>
                  <option value="mail">Mail</option>
                  <option value="share">Share</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-zinc-700 mb-1 block">Color</label>
                <select
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  data-testid="campaign-color-select"
                  className="w-full h-11 px-3 rounded-lg border-2 border-zinc-200 bg-white"
                >
                  <option value="blue">Blue</option>
                  <option value="amber">Amber</option>
                  <option value="green">Green</option>
                  <option value="purple">Purple</option>
                  <option value="teal">Teal</option>
                  <option value="rose">Rose</option>
                  <option value="cyan">Cyan</option>
                  <option value="red">Red</option>
                </select>
              </div>
            </div>

            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
              <input
                type="checkbox"
                checked={form.show_qr}
                onChange={(e) => setForm({ ...form, show_qr: e.target.checked })}
                data-testid="campaign-show-qr-checkbox"
                className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-zinc-800">Show QR code download</div>
                <div className="text-xs text-zinc-500">Best for print materials — flyers, business cards, yard signs.</div>
              </div>
            </label>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => setDialogOpen(false)}
              className="flex-1 h-11 rounded-lg border-2 border-zinc-200 text-zinc-700 font-semibold"
              data-testid="campaign-cancel-button"
            >
              <X className="w-4 h-4 inline mr-1" /> Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 h-11 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold disabled:opacity-50"
              data-testid="campaign-save-button"
            >
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Campaign"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes &quot;{deleteTarget?.name}&quot;. Existing leads already tagged with this UTM source stay intact —
              only the link itself is removed from this page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="campaign-confirm-delete"
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BottomNav />
    </div>
  );
}
